import { Component, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IPayPalConfig, ICreateOrderRequest } from 'ngx-paypal';
import { Observable, of } from 'rxjs';
import { AppModalComponent } from '../../shared/components/app-modal/app-modal.component';
import { MilestonePayoutStatus } from '../../shared/enums/milestone-payout-status';
import { MilestoneProgressStatus } from '../../shared/enums/milestone-progress-status';
import { addMilestoneForm, milestoneFormControls, milestoneValidationMessages } from '../../shared/forms/milestone.form';
import { getDateFromNgbDate, getMinDateAsToday } from '../../shared/helpers/date-picket';
import { getControlValidationMessage } from '../../shared/helpers/form-helper';
import { ModalConfigHelper } from '../../shared/helpers/modal-config-helper';
import { ModalConfig } from '../../shared/models/modal.config';
import { ICreateMilestoneRequestDto } from '../../shared/models/requests/create-milestone.request';
import { IMilestoneResponseDto } from '../../shared/models/responses/milestone.response';
import { UiStateStore } from '../../shared/store/ui-state-store.service';
import { environment } from '../../../environments/environment';
import { JobPayment } from '../../jobs/models/job-payment';
import { DataService } from '../services/data-service.service';
import { WizardStore } from '../stores/wizard-store.service';
import { Currency } from 'src/app/shared/models/currency';
import { AppStore } from 'src/app/shared/store/lookup-store.service';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';


@Component({
  selector: 'app-hire',
  templateUrl: './hire.component.html',
  styleUrls: ['./hire.component.scss']
})
export class HireComponent implements OnInit {
  @ViewChild("paymentModal") private modalComponent: AppModalComponent;
  @ViewChild("confirmationModal") private confirmationModalComponent: AppModalComponent;
  @ViewChild("releasePaymentConfirmationModal") private releasePaymentConfirmationModalComponent: AppModalComponent;
  modalConfig: ModalConfig;
  confirmationModalConfig: ModalConfig;
  
  addMilestoneForm: FormGroup;
  minStartDate = getMinDateAsToday;
  minEndDate = this.minStartDate
  payoutMilestoneId: string;
  today = new Date();

  public selectedCurrency: Currency;
  public form: FormGroup;
  public payPalConfig?: IPayPalConfig;
  public totalAmount: number;
  public currencySymbol: string;
  public paymentProcessed: boolean;
  public paymentFailed: boolean;
  public jobMilestones: IMilestoneResponseDto[];
  
  isFormSubmitted = false;
  
  constructor(private formBuilder: FormBuilder, 
    public wizardStore: WizardStore, 
    private dataService: DataService, 
    private uiState: UiStateStore,
    public lookupStore: AppStore,) {
    this.selectedCurrency = { code: "USD", symbol: "$" } as Currency;
    this.addMilestoneForm = addMilestoneForm();
    this.modalConfig = ModalConfigHelper.getPaymentModalConfig(this.onDismiss, this.onClose);
    this.confirmationModalConfig = ModalConfigHelper.getConfimationModalDialog(this.onConfirmDismiss, this.onConfirmClose);
  }

  ngOnInit(): void {
    
    this.form = this.formBuilder.group({
      amount: ['', [Validators.required, Validators.pattern("^[0-9]*$")]]
    });

    this.wizardStore.selectedCandidateAccountId.subscribe(candidateId => {
      if(!candidateId) {
        return;
      }

      this.dataService.getJobMilestones(this.wizardStore.getSelectedJobId(), candidateId).subscribe(resp => {
        this.initializeJobMilestone(resp);
      });
    });
  }

  onCurrencyChange(currencyCode: string) : void {
    this.selectedCurrency = this.lookupStore.getSelectedCurrency(currencyCode);
  }

  onClose = (): Observable<boolean> => {
    return of<boolean>(true);
  };

  onDismiss = (): Observable<boolean> => {
    return of<boolean>(true);
  };

  onConfirmDismiss = () : boolean => {
    return false;
  };

  onConfirmClose = () : boolean => {
    return true;
  };

  get milestonesFormArray() { return this.addMilestoneForm.controls['milestones'] as FormArray; }

  initializeJobMilestone = (jobMilestones: IMilestoneResponseDto[]) => {
    this.milestonesFormArray.clear();
    this.jobMilestones = jobMilestones;
    if (this.jobMilestones.length > 0) {
      this.jobMilestones.forEach(x => {
        this.milestonesFormArray.push(milestoneFormControls(x));
      });
    } else {
      this.milestonesFormArray.push(milestoneFormControls(null));
    }
  }

  addMilestone = () => {
    this.milestonesFormArray.push(milestoneFormControls(null));
  }

  removeMilestone = (index: number) => {
    this.confirmationModalComponent.open().then(r => {
      if (!r)
        return;

      const milestone = this.jobMilestones[index];
      if (!milestone) {
        this.milestonesFormArray.removeAt(index);
        return;
      }

      this.dataService.deleteMilestone(milestone.id).subscribe(resp => {
        if (resp) {
          const milestoneIndex = this.jobMilestones.findIndex(x => x.id === milestone.id);
          this.jobMilestones.splice(milestoneIndex, 1);
          this.milestonesFormArray.removeAt(index);
          this.uiState.setSuccess("Milestone deleted successfully!");
        }
      });
    });
  }

  getValidationMessage = (controlName: string, formGroupIndex: number) => {
    var controlErrors = this.milestonesFormArray.controls[formGroupIndex].get(controlName).errors;

    return getControlValidationMessage(controlName, controlErrors, milestoneValidationMessages);
  }

  saveMilestones = () => {
    this.isFormSubmitted = true;

    if (!this.addMilestoneForm.valid) {
      return;
    }

    const milestones = this.addMilestoneForm.value.milestones as ICreateMilestoneRequestDto[];
    const currentJobId = this.wizardStore.getSelectedJobId();
    const candidateId = this.wizardStore.getSelectedCandidateId();
    milestones.forEach(x => {
      x.startDate = getDateFromNgbDate(x.startDate);
      x.endDate = getDateFromNgbDate(x.endDate);
      x.jobId = currentJobId;
      x.candidateId = candidateId;
      x.currencySymbol = this.selectedCurrency.symbol;
    });

    this.dataService.saveMilestones(milestones).subscribe(resp => {
      this.uiState.setSuccess("Milestones saved successfully");
      resp = resp.filter(milestones => milestones.candidateId == this.wizardStore.getSelectedCandidateId())
      this.initializeJobMilestone(resp);
    });
  }

  onStartDateSelect = (startDate: NgbDate) => {
    this.minEndDate = startDate;
  }

  isNewMilestone = (index: number) => {
    const milestone = this.jobMilestones[index];
    return !milestone;
  }

  getStatusText = (index: number) => {
    return this.jobMilestones[index]?.progressStatus.name ?? "";
  }

  getCurrencySymbol = (index: number)=> {
    return this.jobMilestones[index]?.currencySymbol;
  }

  showPayout = (index: number) => {
    const milestone = this.jobMilestones[index];

    if (!milestone) {
      return false;
    }
    return milestone.payoutStatus.id === MilestonePayoutStatus.pending;
  }

  canDeleteMilestone = (index: number) => {
    const milestone = this.jobMilestones[index];

    if (!milestone) {
      return false;
    }

    return milestone.payoutStatus.id === MilestonePayoutStatus.pending && milestone.progressStatus.id === MilestoneProgressStatus.pending;
  }

  isProgressDone = (index: number) => {
    const milestone = this.jobMilestones[index];

    if (!milestone) {
      return false;
    }
    return milestone.progressStatus.id === MilestoneProgressStatus.done;
  }

  refreshMilestone = () => {
    this.dataService.getJobMilestones(this.wizardStore.getSelectedJobId(), this.wizardStore.getSelectedCandidateId()).subscribe(resp => {
      this.initializeJobMilestone(resp);
    });
  }

  handleMilestonePayout = (index: number) => {
    const milestone = this.jobMilestones[index];
    this.currencySymbol = milestone.currencySymbol;
    this.totalAmount = milestone.amount;
    this.payoutMilestoneId = milestone.id;
    this.paymentProcessed = false;
    this.initConfig();
    this.modalComponent.open();
  }

  onProceedClicked(): void {
    if (!this.form.valid) {
      return;
    }

    this.totalAmount = Number.parseFloat(this.form.get("amount").value);
    this.initConfig();
    this.modalComponent.open();
  }

  milestonesCompleted(): boolean {
    if (!this.jobMilestones) {
      return false;
    }

    if (this.jobMilestones.length === 0) {
      return false;
    }

    return this.jobMilestones.length === this.jobMilestones.filter(jb => jb.progressStatus.id === MilestoneProgressStatus.completed).length;
  }

  handleMilestoneStatusChangeEvent = (index: number) => {
    const milestone = this.jobMilestones[index];

    if (!milestone) {
      return false;
    }

    this.releasePaymentConfirmationModalComponent.open().then(r => {
      if(!r)
        return;

        this.dataService.updateMilestoneStatus(milestone.id, MilestoneProgressStatus.completed).subscribe(resp => {
          if (resp) {
            this.jobMilestones = resp;
            this.uiState.setSuccess("Admin has been notified to clear payment.!");
          } else {
            this.uiState.setSuccess("Unable to notify the admin to clear payment.!");
          }
        });
    });
  }

  onFinishClicked(): void {
    this.dataService.finishCandidateJob(this.wizardStore.getSelectedJobId()).subscribe(j => this.wizardStore.step = 5);
  }

  private initConfig(): void {
    this.payPalConfig = {
      currency: this.selectedCurrency.code,
      clientId: environment.paypalConfig.clientId,
      createOrderOnClient: (data) => <ICreateOrderRequest>{
        intent: 'CAPTURE',
        purchase_units: [
          {
            amount: {
              currency_code: this.selectedCurrency.code,
              value: this.totalAmount.toFixed(2),
            }
          }
        ]
      },
      advanced: {
        commit: 'true'
      },
      style: {
        label: 'paypal',
        layout: 'horizontal'
      },
      onApprove: (data, actions) => {
        console.log('onApprove - transaction was approved, but not authorized', data, actions);
        actions.order.get().then((details :string) => {
          console.log('onApprove - you can get full order details inside onApprove: ', details);
        });
      },
      onClientAuthorization: (data) => {
        const payment = {
          amount: this.totalAmount,
          paymentType: "advance",
          jobId: this.wizardStore.getSelectedJobId(),
          milestoneId: this.payoutMilestoneId,
          status: data.status,
          transactionDetails: JSON.stringify(data),
        } as JobPayment;

        this.dataService.savePayment(payment).subscribe(s => {
          this.paymentProcessed = true;
          this.paymentFailed = false;
          this.dataService.hireCandidate(this.wizardStore.getSelectedCandidateId(), this.wizardStore.getSelectedJobId()).subscribe();
          this.refreshMilestone();
          /**
           * For now commenting this code.
           * After payment successfull, it remains on Hire tab.
           * Will introduce a button on Hire tab that will take to next tab
           */

          // setTimeout(() => {
          //   this.wizardStore.step = 5;
          // }, 3000);

        });
      },
      onCancel: (data, actions) => {
        this.paymentFailed = false;
        this.paymentProcessed = false;
      },
      onError: (err) => {
        this.paymentFailed = true;
        this.paymentProcessed = false;
      },
      onClick: (data, actions) => {
      },
    };

  }

}

